/**
 * Nutrition Info Widget
 * Displays nutrition information for products using the Nutrition Agent
 */

import React, { useState, useEffect } from 'react';
import { nutritionAgent } from '@/services/agents';
import { Leaf, TrendingUp, Heart, Info } from 'lucide-react';

interface NutritionInfoWidgetProps {
  produceName: string;
  showHealthScore?: boolean;
}

const NutritionInfoWidget: React.FC<NutritionInfoWidgetProps> = ({
  produceName,
  showHealthScore = true,
}) => {
  const [nutritionData, setNutritionData] = useState<any>(null);
  const [healthScore, setHealthScore] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNutrition = async () => {
      try {
        setLoading(true);
        const data = await nutritionAgent.getProduceNutrition(produceName);

        if (data) {
          setNutritionData(data);
          if (showHealthScore) {
            const score = nutritionAgent.calculateHealthScore(data);
            setHealthScore(score);
          }
        } else {
          setError('Nutrition information not available');
        }
      } catch (err) {
        console.error('Error fetching nutrition:', err);
        setError('Unable to load nutrition information');
      } finally {
        setLoading(false);
      }
    };

    if (produceName) {
      fetchNutrition();
    }
  }, [produceName, showHealthScore]);

  if (loading) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <Leaf size={20} style={styles.icon} />
          <h3 style={styles.title}>Nutrition Information</h3>
        </div>
        <p style={styles.loading}>Loading nutrition data...</p>
      </div>
    );
  }

  if (error || !nutritionData) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <Leaf size={20} style={styles.icon} />
          <h3 style={styles.title}>Nutrition Information</h3>
        </div>
        <p style={styles.error}>{error || 'No data available'}</p>
      </div>
    );
  }

  const nutriments = nutritionData.nutriments || {};

  return (
    <div style={styles.widget}>
      <div style={styles.header}>
        <Leaf size={20} style={styles.icon} />
        <h3 style={styles.title}>Nutrition Information</h3>
      </div>

      {nutritionData.product_name && (
        <p style={styles.productName}>{nutritionData.product_name}</p>
      )}

      {showHealthScore && healthScore > 0 && (
        <div style={styles.healthScoreContainer}>
          <div style={styles.scoreHeader}>
            <Heart size={16} style={styles.heartIcon} />
            <span style={styles.scoreLabel}>Health Score</span>
          </div>
          <div style={styles.scoreBar}>
            <div
              style={{
                ...styles.scoreBarFill,
                width: `${healthScore}%`,
                backgroundColor: healthScore >= 70 ? '#2D5A27' : healthScore >= 50 ? '#D4883A' : '#C4704F',
              }}
            />
          </div>
          <span style={styles.scoreValue}>{Math.round(healthScore)}/100</span>
        </div>
      )}

      {nutritionData.nutriscore_grade && (
        <div style={styles.nutriScore}>
          <span style={styles.nutriLabel}>Nutri-Score:</span>
          <span
            style={{
              ...styles.nutriGrade,
              backgroundColor: getNutriScoreColor(nutritionData.nutriscore_grade),
            }}
          >
            {nutritionData.nutriscore_grade.toUpperCase()}
          </span>
        </div>
      )}

      <div style={styles.nutrimentsList}>
        <h4 style={styles.subtitle}>Per 100g</h4>

        {nutriments.energy_100g && (
          <div style={styles.nutrimentRow}>
            <span style={styles.nutrimentLabel}>Energy</span>
            <span style={styles.nutrimentValue}>{nutriments.energy_100g} kcal</span>
          </div>
        )}

        {nutriments.proteins_100g !== undefined && (
          <div style={styles.nutrimentRow}>
            <span style={styles.nutrimentLabel}>Protein</span>
            <span style={styles.nutrimentValue}>{nutriments.proteins_100g}g</span>
          </div>
        )}

        {nutriments.carbohydrates_100g !== undefined && (
          <div style={styles.nutrimentRow}>
            <span style={styles.nutrimentLabel}>Carbohydrates</span>
            <span style={styles.nutrimentValue}>{nutriments.carbohydrates_100g}g</span>
          </div>
        )}

        {nutriments.sugars_100g !== undefined && (
          <div style={styles.nutrimentRow}>
            <span style={styles.nutrimentLabel}>Sugars</span>
            <span style={styles.nutrimentValue}>{nutriments.sugars_100g}g</span>
          </div>
        )}

        {nutriments.fat_100g !== undefined && (
          <div style={styles.nutrimentRow}>
            <span style={styles.nutrimentLabel}>Fat</span>
            <span style={styles.nutrimentValue}>{nutriments.fat_100g}g</span>
          </div>
        )}

        {nutriments.fiber_100g !== undefined && (
          <div style={styles.nutrimentRow}>
            <span style={styles.nutrimentLabel}>Fiber</span>
            <span style={styles.nutrimentValue}>{nutriments.fiber_100g}g</span>
          </div>
        )}
      </div>

      <div style={styles.footer}>
        <Info size={12} />
        <span style={styles.footerText}>Data from Open Food Facts</span>
      </div>
    </div>
  );
};

const getNutriScoreColor = (grade: string): string => {
  const colors: { [key: string]: string } = {
    a: '#038141',
    b: '#85BB2F',
    c: '#FECB02',
    d: '#EE8100',
    e: '#E63E11',
  };
  return colors[grade.toLowerCase()] || '#999';
};

const styles = {
  widget: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '20px',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
  } as React.CSSProperties,
  icon: {
    color: '#2D5A27',
  } as React.CSSProperties,
  title: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0,
  } as React.CSSProperties,
  productName: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '16px',
    fontStyle: 'italic',
  } as React.CSSProperties,
  loading: {
    fontSize: '14px',
    color: '#999',
    textAlign: 'center' as const,
    padding: '20px',
  } as React.CSSProperties,
  error: {
    fontSize: '14px',
    color: '#C4704F',
    textAlign: 'center' as const,
    padding: '20px',
  } as React.CSSProperties,
  healthScoreContainer: {
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: '#F9FAF8',
    borderRadius: '12px',
  } as React.CSSProperties,
  scoreHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px',
  } as React.CSSProperties,
  heartIcon: {
    color: '#C4704F',
  } as React.CSSProperties,
  scoreLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1a1a',
  } as React.CSSProperties,
  scoreBar: {
    height: '10px',
    backgroundColor: '#E8EDE7',
    borderRadius: '5px',
    overflow: 'hidden',
    marginBottom: '8px',
  } as React.CSSProperties,
  scoreBarFill: {
    height: '100%',
    borderRadius: '5px',
    transition: 'width 0.3s ease',
  } as React.CSSProperties,
  scoreValue: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#2D5A27',
  } as React.CSSProperties,
  nutriScore: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
    padding: '12px',
    backgroundColor: '#F9FAF8',
    borderRadius: '8px',
  } as React.CSSProperties,
  nutriLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1a1a',
  } as React.CSSProperties,
  nutriGrade: {
    display: 'inline-block',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    color: '#fff',
    fontWeight: '700',
    fontSize: '16px',
    textAlign: 'center' as const,
    lineHeight: '32px',
  } as React.CSSProperties,
  subtitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '12px',
  } as React.CSSProperties,
  nutrimentsList: {
    marginBottom: '16px',
  } as React.CSSProperties,
  nutrimentRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #F0F0F0',
  } as React.CSSProperties,
  nutrimentLabel: {
    fontSize: '14px',
    color: '#666',
  } as React.CSSProperties,
  nutrimentValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1a1a',
  } as React.CSSProperties,
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    paddingTop: '12px',
    borderTop: '1px solid #F0F0F0',
  } as React.CSSProperties,
  footerText: {
    fontSize: '12px',
    color: '#999',
  } as React.CSSProperties,
};

export default NutritionInfoWidget;
